import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  listarGesDebMunicipais,
  listarFamiliasDebMunicipais,
  exportarDebMunicipais,
} from '~/Services/Service';

const extrairMensagemErro = err =>
  (err && err.message) ||
  (err && typeof err === 'string' ? err : null) ||
  'Erro ao processar. Tente novamente.';

const RelDebitosMunicipais = () => {
  const [todosGes, setTodosGes] = useState([]);
  const [todasFamilias, setTodasFamilias] = useState([]);
  const [gesSelecionados, setGesSelecionados] = useState([]);
  const [familiasSelecionadas, setFamiliasSelecionadas] = useState([]);

  const [dataIni, setDataIni] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [ultimoBoleto, setUltimoBoleto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erroDataIni, setErroDataIni] = useState('');
  const [erroDataFim, setErroDataFim] = useState('');

  useEffect(() => {
    let ativo = true;
    listarGesDebMunicipais()
      .then(res => {
        if (ativo) setTodosGes(res);
      })
      .catch(err => console.error(extrairMensagemErro(err)));
    listarFamiliasDebMunicipais()
      .then(res => {
        if (ativo) setTodasFamilias(res);
      })
      .catch(err => console.error(extrairMensagemErro(err)));
    return () => {
      ativo = false;
    };
  }, []);

  const opcoesGes = useMemo(
    () =>
      todosGes.map(g => ({
        value: g.codigo_grupo_empresa,
        label: g.apelido,
      })),
    [todosGes]
  );

  const opcoesFamilias = useMemo(() => {
    const codigosGe = gesSelecionados.map(g => g.value);
    const lista =
      codigosGe.length === 0
        ? todasFamilias
        : todasFamilias.filter(f => codigosGe.includes(f.codigo_grupo_empresa));
    return lista.map(f => ({
      value: f.codigo_familia,
      label: f.nome_da_familia,
      codigo_grupo_empresa: f.codigo_grupo_empresa,
    }));
  }, [todasFamilias, gesSelecionados]);

  // Quando GEs mudam, remove famílias selecionadas que não pertencem mais
  useEffect(() => {
    if (gesSelecionados.length === 0) return;
    const codigosGe = gesSelecionados.map(g => g.value);
    setFamiliasSelecionadas(prev =>
      prev.filter(f => codigosGe.includes(f.codigo_grupo_empresa))
    );
  }, [gesSelecionados]);

  const validarFiltros = () => {
    setErroDataIni('');
    setErroDataFim('');
    if (!dataIni) {
      setErroDataIni('Data venda inicial deve ser informada.');
      return false;
    }
    if (!dataFim) {
      setErroDataFim('Data venda final deve ser informada.');
      return false;
    }
    if (new Date(dataIni) > new Date(dataFim)) {
      setErroDataIni('Data inicial deve ser menor ou igual à data final.');
      return false;
    }
    return true;
  };

  const exportar = async () => {
    if (!validarFiltros()) return;
    setCarregando(true);
    try {
      const filtro = {
        ges:
          gesSelecionados.length > 0
            ? gesSelecionados.map(g => g.value).join(',')
            : null,
        familias:
          familiasSelecionadas.length > 0
            ? familiasSelecionadas.map(f => f.value).join(',')
            : null,
        data_ini: dataIni,
        data_fim: dataFim,
        ultimo_boleto: ultimoBoleto,
      };
      const res = await exportarDebMunicipais(filtro);
      if (res.status === 204) {
        toast.info('Nenhuma informação encontrada para os filtros selecionados.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'DebitosMunicipais.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregando(false);
    }
  };

  const limpar = () => {
    setGesSelecionados([]);
    setFamiliasSelecionadas([]);
    setDataIni('');
    setDataFim('');
    setUltimoBoleto(false);
    setErroDataIni('');
    setErroDataFim('');
  };

  return (
    <div className="container-fluid mt-3">
      <h4 className="mb-3">Relatório de Débitos Municipais</h4>
      <Card>
        <Card.Body>
          <Row className="mb-3">
            <Col xs={12} md={6} className="mb-3 mb-md-0">
              <Form.Label className="small mb-1">GE (Grupo Empresa)</Form.Label>
              <Select
                isMulti
                placeholder="Todos..."
                options={opcoesGes}
                value={gesSelecionados}
                onChange={selecionados => setGesSelecionados(selecionados || [])}
                styles={customStyles}
                components={{ DropdownIndicator }}
                noOptionsMessage={() => 'Nenhuma opção'}
                closeMenuOnSelect={false}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="small mb-1">Empreendimento</Form.Label>
              <Select
                isMulti
                placeholder="Todos..."
                options={opcoesFamilias}
                value={familiasSelecionadas}
                onChange={selecionados =>
                  setFamiliasSelecionadas(selecionados || [])
                }
                styles={customStyles}
                components={{ DropdownIndicator }}
                noOptionsMessage={() => 'Nenhuma opção'}
                closeMenuOnSelect={false}
              />
            </Col>
          </Row>

          <Row className="mb-3 align-items-end">
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="small mb-1" htmlFor="dataIni">
                Data Venda Inicial
              </Form.Label>
              <input
                type="date"
                id="dataIni"
                className="form-control"
                value={dataIni}
                onChange={e => setDataIni(e.target.value)}
              />
              {erroDataIni && (
                <span className="text-danger small">{erroDataIni}</span>
              )}
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="small mb-1" htmlFor="dataFim">
                Data Venda Final
              </Form.Label>
              <input
                type="date"
                id="dataFim"
                className="form-control"
                value={dataFim}
                onChange={e => setDataFim(e.target.value)}
              />
              {erroDataFim && (
                <span className="text-danger small">{erroDataFim}</span>
              )}
            </Col>
            <Col xs={12} sm={6} md={3} className="d-flex align-items-center mt-3 mt-md-0">
              <Form.Check
                type="checkbox"
                id="ultimoBoleto"
                label="Último boleto do imóvel"
                checked={ultimoBoleto}
                onChange={e => setUltimoBoleto(e.target.checked)}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <Button
                variant="primary"
                onClick={exportar}
                disabled={carregando}
                className="mr-2"
              >
                {carregando ? 'Exportando...' : 'Exportar Excel'}
              </Button>
              <Button variant="secondary" onClick={limpar} disabled={carregando}>
                Limpar
              </Button>
            </Col>
          </Row>

          {carregando && <Loading />}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RelDebitosMunicipais;
